import jsPDF from 'jspdf';
import 'jspdf-autotable';

const fmt = (v) => `Rs.${parseFloat(v||0).toLocaleString('en-IN',{minimumFractionDigits:2})}`;
const fDate = (d) => d ? new Date(d).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'}) : '—';

// ─── ALWAYS light theme for PDFs ─────────────────────────────────────────────
const C = {
  bg:       [255,255,255],
  bg2:      [248,249,252],
  bg3:      [241,243,248],
  border:   [220,222,232],
  text:     [22,24,46],
  text2:    [90,94,120],
  text3:    [150,155,180],
  accent:   [91,83,240],
  accent2:  [123,117,255],
  ok:       [22,163,74],
  warn:     [217,119,6],
  err:      [220,38,38],
  white:    [255,255,255],
};

export async function generatePDF(doc, type, user) {
  const pdf = new jsPDF({ orientation:'p', unit:'mm', format:'a4' });
  const W = 210, H = 297, M = 14;

  const isInvoice = type === 'invoice';
  const docNum = isInvoice ? doc.invoice_number : doc.quotation_number;

  // ── Background ──
  pdf.setFillColor(...C.bg);
  pdf.rect(0,0,W,H,'F');

  // ── Header band ──
  pdf.setFillColor(...C.bg2);
  pdf.rect(0,0,W,50,'F');
  pdf.setFillColor(...C.accent);
  pdf.rect(0,50,W,1.5,'F');

  // ── Accent left bar ──
  pdf.setFillColor(...C.accent);
  pdf.rect(0,0,3,50,'F');

  // ── Logo or company initial ──
  let logoLoaded = false;
  if (user?.company_logo) {
    try {
      const base = process.env.REACT_APP_API_URL || '/api';
      const imgUrl = `${base}/uploads/${user.company_logo}`;
      const imgData = await imageToBase64(imgUrl);
      if (imgData) { pdf.addImage(imgData,'JPEG', M+3, 8, 30, 15, undefined,'FAST'); logoLoaded = true; }
    } catch {}
  }
  if (!logoLoaded) {
    pdf.setFillColor(...C.accent);
    pdf.roundedRect(M+3,8,18,18,3,3,'F');
    pdf.setTextColor(...C.white); pdf.setFontSize(11); pdf.setFont('helvetica','bold');
    pdf.text((user?.company_name?.[0] || 'Q').toUpperCase(), M+12, 20, {align:'center'});
  }

  // ── Company name ──
  const cx = logoLoaded ? M+36 : M+25;
  pdf.setTextColor(...C.text); pdf.setFontSize(14); pdf.setFont('helvetica','bold');
  pdf.text(user?.company_name || 'Your Company', cx, 16);
  pdf.setFontSize(7.5); pdf.setFont('helvetica','normal'); pdf.setTextColor(...C.text2);
  const cLines = [user?.company_address, user?.company_phone&&`📞 ${user.company_phone}`, user?.company_email&&`✉ ${user.company_email}`, user?.company_gst&&`GST: ${user.company_gst}`].filter(Boolean);
  cLines.forEach((l,i) => pdf.text(l, cx, 22 + i*4.5));

  // ── Doc type chip ──
  const chipW = 30, chipX = W-M-chipW, chipY = 8;
  pdf.setFillColor(...(isInvoice ? C.ok : C.accent));
  pdf.roundedRect(chipX, chipY, chipW, 9, 2, 2,'F');
  pdf.setTextColor(...C.white); pdf.setFontSize(7); pdf.setFont('helvetica','bold');
  pdf.text(isInvoice ? 'INVOICE' : 'QUOTATION', chipX+chipW/2, chipY+6, {align:'center'});
  pdf.setTextColor(...C.text); pdf.setFontSize(11); pdf.setFont('helvetica','bold');
  pdf.text(docNum, W-M, 28, {align:'right'});
  pdf.setFontSize(7.5); pdf.setFont('helvetica','normal'); pdf.setTextColor(...C.text2);
  const dLines = [[isInvoice?'Issue Date':'Issue Date', fDate(doc.issue_date)],[isInvoice?'Due Date':'Valid Until',fDate(isInvoice?doc.due_date:doc.valid_until)]];
  dLines.forEach(([k,v],i) => pdf.text(`${k}: ${v}`, W-M, 34+i*5, {align:'right'}));

  // ── Bill To box ──
  let y = 56;
  pdf.setFillColor(...C.bg3); pdf.roundedRect(M, y, (W-M*2)*0.55, 30, 2, 2,'F');
  pdf.setTextColor(...C.text3); pdf.setFontSize(7); pdf.setFont('helvetica','bold');
  pdf.text('BILL TO', M+5, y+6);
  pdf.setTextColor(...C.text); pdf.setFontSize(10); pdf.setFont('helvetica','bold');
  pdf.text(doc.client_name || '', M+5, y+13);
  pdf.setFont('helvetica','normal'); pdf.setFontSize(7.5); pdf.setTextColor(...C.text2);
  [doc.client_phone&&`📞 ${doc.client_phone}`, doc.client_email&&`✉ ${doc.client_email}`, doc.client_gst&&`GST: ${doc.client_gst}`, doc.client_address].filter(Boolean).forEach((l,i) => pdf.text(l, M+5, y+19+i*4.5, {maxWidth:(W-M*2)*0.55-10}));

  y += 36;

  // ── Items table ──
  const items = doc.items || [];
  pdf.autoTable({
    startY: y,
    head: [['#','Item / Description','Qty','Unit Price','Tax %','Amount']],
    body: items.map((it,i) => [
      i+1,
      it.item_name + (it.description ? `\n${it.description}` : ''),
      parseFloat(it.quantity),
      fmt(it.unit_price),
      `${parseFloat(it.tax_percent).toFixed(1)}%`,
      fmt(it.total)
    ]),
    theme: 'plain',
    styles: { font:'helvetica', fontSize:8, textColor:C.text2, cellPadding:5, fillColor:C.bg, lineColor:C.border, lineWidth:0.15 },
    headStyles: { textColor:C.accent, fontStyle:'bold', fontSize:7, fillColor:C.bg3 },
    columnStyles: {
      0:{cellWidth:8},1:{cellWidth:'auto'},2:{cellWidth:14,halign:'center'},
      3:{cellWidth:28,halign:'right'},4:{cellWidth:16,halign:'center'},
      5:{cellWidth:28,halign:'right',textColor:C.text,fontStyle:'bold'}
    },
    alternateRowStyles:{fillColor:C.bg2},
    margin:{left:M,right:M}
  });

  y = pdf.lastAutoTable.finalY + 6;

  // ── Totals ──
  const tw = 68, tx = W-M-tw;
  const rows = [['Subtotal',fmt(doc.subtotal)],['Tax',fmt(doc.tax_total)],['Discount',`- ${fmt(doc.discount)}`]];
  pdf.setFillColor(...C.bg3); pdf.roundedRect(tx, y, tw, rows.length*8+18, 2,2,'F');
  rows.forEach(([lbl,val],i) => {
    pdf.setFontSize(8); pdf.setFont('helvetica','normal'); pdf.setTextColor(...C.text2);
    pdf.text(lbl, tx+4, y+7+i*8);
    pdf.setTextColor(...C.text); pdf.text(val, tx+tw-4, y+7+i*8, {align:'right'});
  });
  const totY = y + rows.length*8+5;
  pdf.setFillColor(...C.accent); pdf.roundedRect(tx, totY, tw, 12, 2,2,'F');
  pdf.setTextColor(...C.white); pdf.setFontSize(9); pdf.setFont('helvetica','bold');
  pdf.text('TOTAL', tx+4, totY+8);
  pdf.text(fmt(doc.total), tx+tw-4, totY+8, {align:'right'});

  // ── Notes ──
  if (doc.notes) {
    const ny = Math.max(totY+18, y+6);
    pdf.setFillColor(...C.bg3); pdf.roundedRect(M, ny, tx-M-6, 20, 2,2,'F');
    pdf.setFontSize(7); pdf.setFont('helvetica','bold'); pdf.setTextColor(...C.text3);
    pdf.text('NOTES', M+4, ny+6);
    pdf.setFont('helvetica','normal'); pdf.setFontSize(7.5); pdf.setTextColor(...C.text2);
    pdf.text(doc.notes, M+4, ny+12, {maxWidth: tx-M-14});
  }

  // ── Signature ──
  const sigY = H-52;
  pdf.setDrawColor(...C.border); pdf.setLineWidth(0.3);
  pdf.line(M, sigY+18, M+55, sigY+18);
  pdf.setFontSize(7); pdf.setTextColor(...C.text3); pdf.setFont('helvetica','normal');
  pdf.text('Authorised Signatory', M, sigY+23);
  if (user?.signer_name) { pdf.setFont('helvetica','bold'); pdf.setTextColor(...C.text); pdf.setFontSize(8); pdf.text(user.signer_name, M, sigY+29); }
  if (user?.signer_desig) { pdf.setFont('helvetica','normal'); pdf.setFontSize(7); pdf.setTextColor(...C.text2); pdf.text(user.signer_desig, M, sigY+34); }

  // Signature image
  if (user?.signature_img) {
    try {
      const base = process.env.REACT_APP_API_URL || '/api';
      const sigData = await imageToBase64(`${base}/uploads/${user.signature_img}`);
      if (sigData) pdf.addImage(sigData,'PNG', M, sigY-12, 40, 12, undefined,'FAST');
    } catch {}
  }

  // ── Footer band ──
  pdf.setFillColor(...C.bg3); pdf.rect(0,H-16,W,16,'F');
  pdf.setFillColor(...C.accent); pdf.rect(0,H-16,W,1,'F');
  pdf.setTextColor(...C.text3); pdf.setFontSize(7); pdf.setFont('helvetica','normal');
  pdf.text('Thank you for your business!', W/2, H-9, {align:'center'});
  pdf.text(`Generated by QuotePro • ${new Date().toLocaleDateString('en-IN')}`, W/2, H-4, {align:'center'});

  pdf.save(`${docNum}.pdf`);
}

async function imageToBase64(url) {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const c = document.createElement('canvas');
      c.width = img.width; c.height = img.height;
      c.getContext('2d').drawImage(img,0,0);
      resolve(c.toDataURL('image/jpeg',0.85));
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });
}
